import React, { useState, useEffect } from 'react';
import { 
  UserGroupIcon, 
  ClockIcon, 
  HeartIcon, 
  ExclamationTriangleIcon,
  CheckCircleIcon,
  BuildingOffice2Icon
} from '@heroicons/react/24/outline';
import { opdAdmissionAPI, queueAPI, reportsAPI, bedAPI } from '../../api';

const DashboardOverview = () => {
  const [stats, setStats] = useState({
    admissions: {
      total: 0,
      active: 0,
      pending: 0,
      discharged: 0
    },
    queue: {
      waiting: 0,
      inProgress: 0,
      completed: 0
    },
    beds: {
      total: 0,
      occupied: 0,
      available: 0,
      maintenance: 0
    },
    emergencies: {
      active: 0,
      critical: 0
    }
  });
  const [loading, setLoading] = useState(true);
  const [recentActivities, setRecentActivities] = useState([]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Load admission statistics
      const admissionStats = await opdAdmissionAPI.getStatistics();
      
      // Load bed statistics
      const bedStats = await bedAPI.getStatistics();
      
      // Load reports data
      const reportsData = await reportsAPI.getDashboardSummary();
      
      setStats({
        admissions: admissionStats.data || stats.admissions,
        beds: bedStats.data || stats.beds,
        queue: reportsData.data?.queue || stats.queue,
        emergencies: reportsData.data?.emergencies || stats.emergencies
      });

      setRecentActivities(reportsData.data?.recentActivities || []);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, color, bgColor }) => (
    <div className={`${bgColor} rounded-xl p-6 shadow-lg border border-gray-200`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className={`text-3xl font-bold ${color} mt-1`}>{value}</p>
          {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-lg ${color.replace('text-', 'bg-').replace('-600', '-100')}`}>
          <Icon className={`w-6 h-6 ${color}`} />
        </div>
      </div>
    </div>
  );

  const ActivityItem = ({ activity }) => (
    <div className="flex items-center py-3 border-b border-gray-100 last:border-b-0">
      <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
      <div className="flex-1">
        <p className="text-sm font-medium text-gray-900">{activity.title}</p>
        <p className="text-xs text-gray-500">{activity.time}</p>
      </div>
      <span className={`px-2 py-1 text-xs rounded-full ${
        activity.type === 'admission' ? 'bg-green-100 text-green-800' :
        activity.type === 'discharge' ? 'bg-blue-100 text-blue-800' :
        activity.type === 'emergency' ? 'bg-red-100 text-red-800' :
        'bg-gray-100 text-gray-800'
      }`}>
        {activity.type}
      </span>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Nurse Dashboard</h1>
        <p className="text-gray-600 mt-2">Outpatient Department - Overview and Statistics</p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Active Admissions"
          value={stats.admissions.active}
          subtitle={`${stats.admissions.pending} pending`}
          icon={UserGroupIcon}
          color="text-blue-600"
          bgColor="bg-white"
        />
        
        <StatCard
          title="Queue Status"
          value={stats.queue.waiting}
          subtitle={`${stats.queue.inProgress} in progress`}
          icon={ClockIcon}
          color="text-orange-600"
          bgColor="bg-white"
        />
        
        <StatCard
          title="Available Beds"
          value={stats.beds.available}
          subtitle={`${stats.beds.occupied}/${stats.beds.total} occupied`}
          icon={BuildingOffice2Icon}
          color="text-green-600"
          bgColor="bg-white"
        />
        
        <StatCard
          title="Active Emergencies"
          value={stats.emergencies.active}
          subtitle={`${stats.emergencies.critical} critical`}
          icon={ExclamationTriangleIcon}
          color="text-red-600"
          bgColor="bg-white"
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Activities */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-lg border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Recent Activities</h3>
          </div>
          <div className="p-6">
            {recentActivities.length > 0 ? (
              <div className="space-y-1">
                {recentActivities.map((activity, index) => (
                  <ActivityItem key={index} activity={activity} />
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <CheckCircleIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No recent activities</p>
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Quick Actions</h3>
          </div>
          <div className="p-6 space-y-3">
            <button className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200">
              New Admission
            </button>
            <button className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200">
              Record Vitals
            </button>
            <button className="w-full bg-orange-600 hover:bg-orange-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200">
              Manage Queue
            </button>
            <button className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200">
              Emergency Alert
            </button>
          </div>
        </div>
      </div>

      {/* Additional Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white">
          <h4 className="text-lg font-semibold mb-2">Today's Admissions</h4>
          <p className="text-3xl font-bold">{stats.admissions.total}</p>
          <p className="text-blue-100 text-sm mt-1">+12% from yesterday</p>
        </div>
        
        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-6 text-white">
          <h4 className="text-lg font-semibold mb-2">Completed Visits</h4>
          <p className="text-3xl font-bold">{stats.queue.completed}</p>
          <p className="text-green-100 text-sm mt-1">+8% from yesterday</p>
        </div>
        
        <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-6 text-white">
          <h4 className="text-lg font-semibold mb-2">Bed Utilization</h4>
          <p className="text-3xl font-bold">
            {stats.beds.total > 0 ? Math.round((stats.beds.occupied / stats.beds.total) * 100) : 0}%
          </p>
          <p className="text-purple-100 text-sm mt-1">Optimal range: 70-85%</p>
        </div>
      </div>
    </div>
  );
};

export default DashboardOverview;